/**
 * Auto-resolution functions for validation errors.
 *
 * Each resolver is a pure function: (article, error) → WireArticleFull.
 * They never mutate in-place. The shell applies the result via setLocalArticle.
 *
 * Only errors where the correct fix is deterministic should have a resolver.
 * Ambiguous cases (e.g. which duplicate section-marker to remove) are left
 * without a resolver so the reviewer must make the call manually.
 */

import type { WireArticleBlock, WireArticleFull } from '../../../api/types'
import type { ValidationError } from './types'

// ─── Resolver type ────────────────────────────────────────────────────────────

type ResolverFn = (article: WireArticleFull, error: ValidationError) => WireArticleFull

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapBlock(
  article: WireArticleFull,
  blockId: string,
  fn: (block: WireArticleBlock) => WireArticleBlock | null
): WireArticleFull {
  const blocks: WireArticleBlock[] = []
  for (const block of article.blocks) {
    if (block.id === blockId) {
      const result = fn(block)
      if (result !== null) blocks.push(result)
    } else {
      blocks.push(block)
    }
  }
  return { ...article, blocks }
}

function deleteBlock(article: WireArticleFull, error: ValidationError): WireArticleFull {
  if (!('blockId' in error)) return article
  return mapBlock(article, error.blockId, () => null)
}

// ─── Resolvers ────────────────────────────────────────────────────────────────

const resolvers: Partial<Record<string, ResolverFn>> = {

  // Delete the whole block — it has no useful content
  'paragraph.empty': deleteBlock,
  'paragraph.whitespace-only': deleteBlock,
  'heuristic.trivial-paragraph': deleteBlock,
  'warning.empty': deleteBlock,

  // Remove all empty items from the list
  'list.empty-item': (article, error) => {
    if (!('blockId' in error)) return article
    return mapBlock(article, error.blockId, block => {
      if (block.type !== 'list') return block
      return {
        ...block,
        items: block.items.filter(item => {
          const text = item.children
            .map(c => (c.type === 'span' ? c.text : c.type === 'link' ? c.text : ''))
            .join('')
          return text.trim().length > 0
        }),
      }
    })
  },

  // Remove all entries with empty text from the source list
  'source-list.empty-entry': (article, error) => {
    if (!('blockId' in error)) return article
    return mapBlock(article, error.blockId, block => {
      if (block.type !== 'source-list') return block
      return {
        ...block,
        entries: block.entries.filter(e => e.text.trim().length > 0),
      }
    })
  },

  // Strip leading classification fragment from the first span's text
  'heuristic.classification-bleed': (article, error) => {
    if (!('blockId' in error)) return article
    return mapBlock(article, error.blockId, block => {
      if (block.type === 'paragraph') {
        const [first, ...rest] = block.children
        if (first?.type !== 'span') return block
        // Strip leading )//, )//, " // ", etc.
        const cleaned = first.text.replace(/^\s*\)?\s*\/\/[A-Z\/]+\s*/, '').trimStart()
        return { ...block, children: [{ ...first, text: cleaned }, ...rest] }
      }
      if (block.type === 'heading') {
        const cleaned = block.content.replace(/^\s*\)?\s*\/\/[A-Z\/]+\s*/, '').trimStart()
        return { ...block, content: cleaned }
      }
      if (block.type === 'warning') {
        const cleaned = block.content.replace(/^\s*\)?\s*\/\/[A-Z\/]+\s*/, '').trimStart()
        return { ...block, content: cleaned }
      }
      return block
    })
  },

  // Strip the leading lone closing paren and any adjacent whitespace
  'heuristic.lone-closing-paren': (article, error) => {
    if (!('blockId' in error)) return article
    return mapBlock(article, error.blockId, block => {
      if (block.type === 'paragraph') {
        const [first, ...rest] = block.children
        if (first?.type !== 'span') return block
        const cleaned = first.text.replace(/^\s*\)\s*[A-Z\/]*\s*/, '').trimStart()
        return { ...block, children: [{ ...first, text: cleaned }, ...rest] }
      }
      return block
    })
  },

  // Remove unresolved citation nodes from paragraph / list children
  'citation.unresolved': (article, error) => {
    if (!('blockId' in error)) return article
    // We don't know which sourceId is broken from the error alone —
    // remove all citations whose sourceId doesn't appear in the source list.
    const sourceList = article.blocks.find(b => b.type === 'source-list')
    const knownIds = new Set(
      sourceList?.type === 'source-list' ? sourceList.entries.map(e => e.id) : []
    )
    return mapBlock(article, error.blockId, block => {
      if (block.type === 'paragraph') {
        return {
          ...block,
          children: block.children.filter(
            c => c.type !== 'citation' || knownIds.has(c.sourceId)
          ),
        }
      }
      if (block.type === 'list') {
        return {
          ...block,
          items: block.items.map(item => ({
            ...item,
            children: item.children.filter(
              c => c.type !== 'citation' || knownIds.has(c.sourceId)
            ),
          })),
        }
      }
      return block
    })
  },
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns true if an error code has an available automatic resolution.
 * Used by ValidationAnnotation to show/hide the Auto-fix button.
 */
export function canAutoResolve(code: string): boolean {
  return code in resolvers
}

/**
 * Applies the automatic resolution for the given error.
 * Returns the updated article, or null if no resolver exists for this code.
 */
export function applyResolution(
  article: WireArticleFull,
  error: ValidationError
): WireArticleFull | null {
  const fn = resolvers[error.code]
  if (!fn) return null
  return fn(article, error)
}
